import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Calendar, Trash2, Clock, Plus, User, Target, Search, ChevronLeft, ChevronRight, Archive, Info, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { getAssessmentHistory, DOMAIN_META, type AssessmentHistoryEntry, type TeacherAssignment } from '../../../assessment';
import { TeacherV2Route } from '../TeacherSidebarV2';
import { DjayNarrator } from '../../../mascot';
import { CreateAssessmentPanel } from '../components/CreateAssessmentPanel';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { InsightBar } from '../components/InsightBar';
import { EmptyState } from '../../../components/shared/EmptyState';
import { DataFreshness } from '../../shared/DataFreshness';
import { ClassroomSwitcher } from '../components/ClassroomSwitcher';
import { dataLayer } from '../../../data/dataLayer';
import { formatRelativeTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import type { Account, Classroom } from '../../../accounts';
import { containerVariants, springItemVariants as itemVariants, tableRowVariants } from '../shared/animationVariants';
interface AssessmentsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onNavigate?: (route: TeacherV2Route) => void;
  activeAccount?: Account;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  initialTab?: 'klaarzetten' | 'resultaten';
  initialSearchQuery?: string;
}
type MainTabType = 'klaarzetten' | 'resultaten';
const ITEMS_PER_PAGE = 20;
export function AssessmentsPage({
  students,
  selectedGroup,
  activeAccount,
  classrooms = [],
  selectedClassroomId,
  onSelectClassroom,
  initialTab,
  initialSearchQuery
}: AssessmentsPageProps) {
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [mainTab, setMainTab] = useState<MainTabType>(initialTab || 'klaarzetten');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: async () => {}
  });
  // Apply initial values when they change
  useEffect(() => {
    if (initialTab) setMainTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (initialSearchQuery !== undefined) setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);
  const loadData = useCallback(async () => {
    try {
      const [history, assignmentsData] = await Promise.all([getAssessmentHistory(selectedGroup), dataLayer.getTeacherAssignments()]);
      setAssessmentHistory(history);
      // Filter assignments for this classroom/group
      const allAssignments = Object.values(assignmentsData);
      const relevantAssignments = allAssignments.filter(a => (a.classroomId || null) === (selectedClassroomId || null) || !a.classroomId && a.group === selectedGroup);
      setAssignments(relevantAssignments.sort((a, b) => b.createdAt - a.createdAt));
      setLastLoadedAt(new Date());
    } catch (err) {
      logError('[AssessmentsPage] loadData failed:', err);
      setAssessmentHistory([]);
      setAssignments([]);
    }
  }, [selectedGroup, selectedClassroomId]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDomain]);
  const filteredHistory = useMemo(() => {
    let filtered = assessmentHistory;
    // 1. Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const student = students.find(s => s.id === entry.learnerId);
        const studentName = student ? student.name.toLowerCase() : 'onbekend';
        return studentName.includes(query);
      });
    }
    // 2. Domain / Type filter
    if (selectedDomain === 'niveaubepaling') {
      filtered = filtered.filter(entry => !entry.assignmentId);
    } else if (selectedDomain === 'toegewezen') {
      filtered = filtered.filter(entry => !!entry.assignmentId);
    } else if (selectedDomain !== 'all') {
      filtered = filtered.filter(entry => entry.domainId === selectedDomain);
    }
    return filtered;
  }, [assessmentHistory, searchQuery, selectedDomain, students]);
  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const avgAssessmentScore = assessmentHistory.length > 0 ? Math.round(assessmentHistory.reduce((sum, a) => sum + a.score, 0) / assessmentHistory.length) : 0;
  const uniqueLearnersTested = useMemo(() => {
    const uniqueIds = new Set(assessmentHistory.map(a => a.learnerId));
    return uniqueIds.size;
  }, [assessmentHistory]);
  const handleExportAssessmentCsv = () => {
    const headers = ['Datum', 'Leerling', 'Type', 'Domein', 'Score (%)', 'Aantal Vragen', 'Tijd (min)'];
    const rows = filteredHistory.map(a => {
      const dateStr = new Date(a.completedAt).toLocaleDateString('nl-NL');
      const student = students.find(s => s.id === a.learnerId);
      const studentName = student ? student.name : 'Onbekend';
      const typeLabel = a.assignmentId ? 'Toegewezen toets' : 'Niveaubepaling';
      const domainLabel = a.domainId ? DOMAIN_META[a.domainId]?.label : 'Onbekend';
      return [dateStr, `"${studentName}"`, `"${typeLabel}"`, `"${domainLabel}"`, a.score, a.totalQuestions, Math.round(a.totalTimeSeconds / 60)].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'toetsresultaten_export');
  };
  const handleDeleteAssignment = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Toets verwijderen',
      message: 'Weet je zeker dat je deze toets wilt verwijderen?',
      action: async () => {
        await dataLayer.deleteTeacherAssignment(id);
        loadData();
      }
    });
  };
  const handleArchiveAssignment = async (id: string) => {
    setConfirmAction({
      isOpen: true,
      title: 'Toets archiveren',
      message: 'Weet je zeker dat je deze toets wilt archiveren?',
      action: async () => {
        await dataLayer.archiveTeacherAssignment(id);
        loadData();
      }
    });
  };
  const domainOptions = [{
    value: 'all',
    label: 'Alle Toetsen'
  }, {
    value: 'niveaubepaling',
    label: 'Alleen Niveaubepalingen'
  }, {
    value: 'toegewezen',
    label: 'Alleen Toegewezen Toetsen'
  }, ...Object.entries(DOMAIN_META).map(([id, meta]) => ({
    value: id,
    label: `Domein: ${meta.label}`
  }))];
  return <PageLayout title="Toetsen" description="Toetsresultaten en niveaubepaling" data-id="element-9400">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9401">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-[-12px]" data-id="element-9402">
          {classrooms.length > 0 && onSelectClassroom ? <ClassroomSwitcher classrooms={classrooms} selectedClassroomId={selectedClassroomId || null} onSelectClassroom={onSelectClassroom} activeTeacherId={activeAccount?.id} data-id="element-9403" /> : <span data-id="element-9404" />}
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={loadData} data-id="element-9405" />
        </div>

        {/* Page-level Tab Switcher */}
        <motion.div variants={itemVariants} className="flex justify-center sm:justify-start" data-id="element-9406">
          <div className="bg-slate-100 p-1 rounded-xl inline-flex relative" data-id="element-9407">
            <button onClick={() => setMainTab('klaarzetten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'klaarzetten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-9408">
              {mainTab === 'klaarzetten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-9409" />}
              <span className="relative z-10" data-id="element-9410">Toetsen</span>
            </button>
            <button onClick={() => setMainTab('resultaten')} className={`relative px-6 py-2.5 rounded-lg text-sm font-bold transition-colors z-10 ${mainTab === 'resultaten' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-9411">
              {mainTab === 'resultaten' && <motion.div layoutId="activeTabIndicator" className="absolute inset-0 bg-white rounded-lg shadow-sm" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-9412" />}
              <span className="relative z-10" data-id="element-9413">Resultaten</span>
            </button>
          </div>
        </motion.div>

        {mainTab === 'klaarzetten' && <motion.div key="klaarzetten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-9414">
            {/* Toets klaarzetten CTA */}
            <Card className="p-5 border-l-4 border-l-orange-400 border-slate-200 shadow-sm bg-white flex flex-col sm:flex-row items-start gap-4" data-id="element-9415">
              <DjayNarrator size="md" emotion="pointing" data-id="element-9416" />
              <div className="flex-1" data-id="element-9417">
                <h2 className="text-lg font-black text-slate-900 mb-1" data-id="element-9418">
                  Toets klaarzetten
                </h2>
                <p className="text-sm text-slate-600 mb-3 max-w-xl leading-relaxed" data-id="element-9419">
                  Zet een toets klaar voor je klas of specifieke leerlingen.
                </p>
                <Button onClick={() => setIsCreatePanelOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="sm" data-id="element-9420">
                  <Plus className="w-4 h-4 mr-2" data-id="element-9421" />
                  Nieuwe toets klaarzetten
                </Button>
              </div>
            </Card>

            {/* Active Assignments Section */}
            {assignments.length > 0 ? <div className="space-y-4" data-id="element-9422">
                <h2 className="text-xl font-black text-slate-900" data-id="element-9423">
                  Klaargezette Toetsen
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9424">
                  {assignments.filter(a => a.status !== 'archived').map(assignment => {
              const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
              const targetCount = assignment.wholeClass ? students.length : assignment.targetStudentIds.length;
              // Count completions (matching domain and completed after assignment creation)
              const completions = assessmentHistory.filter(a => a.domainId === assignment.domainId && a.completedAt > assignment.createdAt).length;
              const completedCount = Math.min(completions, targetCount);
              const progressPercent = targetCount > 0 ? Math.round(completedCount / targetCount * 100) : 0;
              let status = assignment.status;
              if (assignment.deadline && Date.now() > assignment.deadline) status = 'expired';
              if (completedCount >= targetCount && targetCount > 0) status = 'completed';
              const Icon = meta?.icon;
              return <Card key={assignment.id} className="p-4 border-slate-200 flex flex-col bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-9425">
                          <div className="flex items-start justify-between mb-3" data-id="element-9426">
                            <div className="flex items-center gap-3" data-id="element-9427">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-9428">
                                {Icon ? <Icon className="w-4 h-4" data-id="element-9429" /> : '📝'}
                              </div>
                              <div data-id="element-9430">
                                <div className="flex items-center gap-2" data-id="element-9431">
                                  <h3 className="font-bold text-sm text-slate-900" data-id="element-9432">
                                    {assignment.domainLabel}
                                  </h3>
                                  {assignment.type === 'general' && <Badge variant="default" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] py-0 px-1.5" data-id="element-9433">
                                      Algemeen
                                    </Badge>}
                                  {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] py-0 px-1.5" data-id="element-9434">
                                      Gericht
                                    </Badge>}
                                </div>
                                {assignment.type === 'targeted' && assignment.selectedSkillSummary && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1" data-id="element-9435">
                                      {assignment.selectedSkillSummary}
                                    </div>}
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-1" data-id="element-9436">
                                  <Calendar className="w-3 h-3" data-id="element-9437" />
                                  {new Date(assignment.createdAt).toLocaleDateString('nl-NL')}
                                  {assignment.deadline && <>
                                      <span data-id="element-9438">•</span>
                                      <span className="text-orange-600" data-id="element-9439">
                                        Deadline:{' '}
                                        {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-9440">
                              <button onClick={() => handleArchiveAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors" aria-label="Archiveren" title="Archiveren" data-id="element-9441">
                                <Archive className="w-4 h-4" data-id="element-9442" />
                              </button>
                              <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" aria-label="Verwijderen" data-id="element-9443">
                                <Trash2 className="w-4 h-4" data-id="element-9444" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-auto space-y-2" data-id="element-9445">
                            <div className="flex items-center justify-between text-xs" data-id="element-9446">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-9447">
                                <span className="text-slate-600 font-medium" data-id="element-9448">
                                  {completedCount} van {targetCount} voltooid
                                </span>
                                {/*
                                 F-AS-R1-ASSIGNED-ASSESSMENT-EXIT: surface
                                 students who started and abandoned this
                                 assignment so the teacher can follow up.
                                 Hidden when zero — keeps the card calm.
                                 Filters out students who later completed.
                                 */}
                                {(() => {
                        const abandoned = assignment.abandonedByStudentIds || [];
                        const completedSet = new Set(assignment.completedByStudentIds || []);
                        const stillAbandoned = abandoned.filter(sid => !completedSet.has(sid));
                        if (stillAbandoned.length === 0) return null;
                        return <span className="inline-flex items-center gap-1 text-amber-700 font-medium" title={`${stillAbandoned.length} leerling${stillAbandoned.length === 1 ? '' : 'en'} is gestopt voor het einde — mogelijk een gesprek waard.`} data-id="element-9449">
                                      <AlertTriangle className="w-3 h-3" data-id="element-9450" />
                                      {stillAbandoned.length} gestopt
                                    </span>;
                      })()}
                              </div>
                              {status === 'active' && <Badge variant="default" className="bg-indigo-100 text-indigo-700 text-[10px] py-0 px-1.5" data-id="element-9451">
                                  Loopt nu
                                </Badge>}
                              {status === 'completed' && <Badge variant="success" className="bg-emerald-100 text-emerald-700 text-[10px] py-0 px-1.5" data-id="element-9452">
                                  Afgerond
                                </Badge>}
                              {status === 'expired' && <Badge variant="danger" className="bg-rose-100 text-rose-700 text-[10px] py-0 px-1.5" data-id="element-9453">
                                  Deadline voorbij
                                </Badge>}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-9454">
                              <div className={`h-full rounded-full transition-all duration-1000 ${status === 'completed' ? 'bg-emerald-500' : status === 'expired' ? 'bg-rose-500' : 'bg-indigo-500'}`} style={{
                      width: `${progressPercent}%`
                    }} data-id="element-9455" />
                            </div>
                          </div>
                        </Card>;
            })}
                </div>
              </div> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-9456">
                <EmptyState title="Geen actieve toetsen" message="Je hebt op dit moment geen toetsen klaargezet voor je leerlingen." emotion="happy" colorScheme="slate" data-id="element-9457" />
              </motion.div>}
          </motion.div>}

        {mainTab === 'resultaten' && <motion.div key="resultaten-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-9458">
            {/* Insight Bar */}
            <InsightBar message={<>
                  <p data-id="element-9460">
                    {assessmentHistory.length === 0 ? 'Er zijn nog geen toetsen afgenomen. Zet een toets klaar om te beginnen!' : `Er zijn ${assessmentHistory.length} toetsen afgenomen bij ${uniqueLearnersTested} leerlingen. De gemiddelde score is ${avgAssessmentScore}%.`}
                  </p>
                  <p className="text-xs text-indigo-500/70 mt-1.5 flex items-center gap-1" data-id="element-9461">
                    <Info className="w-3 h-3" data-id="element-9462" />
                    Indicatief — geen officiële Cito-toets.
                  </p>
                </>} data-id="element-9459" />

            {/* Toetsresultaten Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-9463">
              <div data-id="element-9464">
                <h2 className="text-xl font-black text-slate-900 mb-1" data-id="element-9465">
                  Toetsresultaten Historie
                </h2>
                <p className="text-sm text-slate-500" data-id="element-9466">
                  Overzicht van alle gemaakte toetsen in deze groep.
                </p>
              </div>
              <Button onClick={handleExportAssessmentCsv} variant="outline" disabled={filteredHistory.length === 0} size="sm" className="border-slate-200 text-slate-600 hover:bg-slate-50" data-id="element-9467">
                <Download className="w-4 h-4 mr-2" data-id="element-9468" />
                Exporteer CSV
              </Button>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-id="element-9469">
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-9470">
                <Input placeholder="Zoek leerling..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-9472" />} size="sm" data-id="element-9471" />
              </div>
              <div className="w-full sm:w-1/2 md:w-1/3" data-id="element-9473">
                <Select options={domainOptions} value={selectedDomain} onChange={e => setSelectedDomain(e.target.value)} size="sm" data-id="element-9474" />
              </div>
            </div>

            {paginatedHistory.length > 0 ? <>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" variants={containerVariants} initial="hidden" animate="visible" data-id="element-9475">
                  {paginatedHistory.map((entry, idx) => {
              const dateStr = new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });
              const relativeDate = formatRelativeTime(entry.completedAt);
              const isHigh = entry.score >= 70;
              const isMedium = entry.score >= 50 && entry.score < 70;
              const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
              const student = students.find(s => s.id === entry.learnerId);
              const studentName = student ? student.name : 'Onbekend';
              return <motion.div key={entry.attemptId} variants={tableRowVariants} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-9476">
                        <div className="flex justify-between items-start mb-3" data-id="element-9477">
                          <div className="flex items-center gap-3" data-id="element-9478">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 border border-slate-200" data-id="element-9479">
                              <User className="w-5 h-5" data-id="element-9480" />
                            </div>
                            <div data-id="element-9481">
                              <div className="font-bold text-slate-900" data-id="element-9482">
                                {studentName}
                              </div>
                              <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-9483">
                                <Clock className="w-3 h-3" data-id="element-9484" />
                                {relativeDate}{' '}
                                <span className="text-slate-300" data-id="element-9485">•</span>{' '}
                                {dateStr}
                              </div>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-9486">
                            <div className={`text-2xl font-black ${isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-9487">
                              {entry.score}%
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between" data-id="element-9488">
                          <div className="flex items-center gap-2" data-id="element-9489">
                            {domainMeta ? <div className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-9490">
                                <domainMeta.icon className="w-3.5 h-3.5" data-id="element-9491" />
                                {domainMeta.label}
                              </div> : <div className="flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-600" data-id="element-9492">
                                <Target className="w-3.5 h-3.5" data-id="element-9493" />
                                Algemeen
                              </div>}

                            {entry.assignmentId ? <span className="text-[10px] font-bold text-orange-600 uppercase tracking-wider" data-id="element-9494">
                                Toegewezen
                              </span> : <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-9495">
                                Niveaubepaling
                              </span>}
                          </div>

                          <div className="text-xs text-slate-500 font-medium" data-id="element-9496">
                            {entry.totalQuestions} vr{' '}
                            <span className="text-slate-300 mx-1" data-id="element-9497">•</span>{' '}
                            {Math.round(entry.totalTimeSeconds / 60)} min
                          </div>
                        </div>
                      </motion.div>;
            })}
                </motion.div>

                {/* Pagination Controls */}
                {totalPages > 1 && <div className="flex items-center justify-between border-t border-slate-100 pt-4 mb-6" data-id="element-9498">
                    <div className="text-sm text-slate-500" data-id="element-9499">
                      Pagina{' '}
                      <span className="font-bold text-slate-900" data-id="element-9500">
                        {currentPage}
                      </span>{' '}
                      van{' '}
                      <span className="font-bold text-slate-900" data-id="element-9501">
                        {totalPages}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-9502">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2" data-id="element-9503">
                        <ChevronLeft className="w-4 h-4" data-id="element-9504" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2" data-id="element-9505">
                        <ChevronRight className="w-4 h-4" data-id="element-9506" />
                      </Button>
                    </div>
                  </div>}
              </> : <motion.div className="mb-6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} data-id="element-9507">
                <EmptyState title="Geen resultaten gevonden" message="Er zijn geen toetsen die voldoen aan je huidige filters." emotion="happy" colorScheme="slate" data-id="element-9508" />
              </motion.div>}
          </motion.div>}
      </motion.div>

      <CreateAssessmentPanel open={isCreatePanelOpen} onClose={() => setIsCreatePanelOpen(false)} students={students} selectedGroup={selectedGroup} teacherId={activeAccount?.id || ''} classroomId={selectedClassroomId || null} onSuccess={loadData} data-id="element-9509" />

      <ConfirmDialog open={confirmAction.isOpen} title={confirmAction.title} message={confirmAction.message} onConfirm={async () => {
      await confirmAction.action();
      setConfirmAction(prev => ({
        ...prev,
        isOpen: false
      }));
    }} onCancel={() => setConfirmAction(prev => ({
      ...prev,
      isOpen: false
    }))} data-id="element-9510" />
    </PageLayout>;
}